/**
 * @ngdoc service
 * @name iframePopupAuthService
 * @module flowingly.runner
 *
 * @description Service to handle popup-based authentication for iframe contexts.
 * When the app is embedded in an iframe and storage partitioning is in effect,
 * this service handles opening a popup for login and receiving tokens via postMessage.
 */
import angular from 'angular';
import { SharedAngular } from '@Client/@types/sharedAngular';

const POPUP_AUTH_MESSAGE_TYPE = 'FLOWINGLY_IFRAME_AUTH_SUCCESS';
const POPUP_MODE_STORAGE_KEY = 'flowingly_popup_auth_mode';
const POPUP_WIDTH = 500;
const POPUP_HEIGHT = 700;

export interface IframeAuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

interface Auth0TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token: string;
  expires_in: number;
  token_type: string;
  scope: string;
}

export class IframePopupAuthService {
  public static $inject = [
    '$q',
    '$window',
    '$http',
    'APP_CONFIG',
    'tokenService',
    'authLoggingApiService'
  ];

  constructor(
    private $q: angular.IQService,
    private $window: angular.IWindowService,
    private $http: angular.IHttpService,
    private APP_CONFIG: SharedAngular.APP_CONFIG,
    private tokenService: SharedAngular.TokenService,
    private authLoggingApiService: SharedAngular.AuthLoggingApiService
  ) {}

  /**
   * Check if the app is running inside an iframe
   */
  isInIframe(): boolean {
    try {
      return this.$window !== this.$window.parent;
    } catch {
      // If we can't access window.parent due to cross-origin, we're in an iframe
      return true;
    }
  }

  /**
   * Determine if we should use popup login based on settings and context
   */
  shouldUsePopupLogin(): boolean {
    const usePopup =
      this.APP_CONFIG.enableIframePopupLogin &&
      this.APP_CONFIG.auth0UseRefreshToken &&
      this.isInIframe();

    // If we're in iframe popup mode, set the skip token refresh flag
    // because Auth0 SDK token refresh doesn't work in iframes due to storage partitioning
    if (usePopup) {
      this.tokenService.setSkipTokenRefresh(true);
    }

    return usePopup;
  }

  /**
   * Open a popup window for login
   * @param redirectUrl The URL to redirect to after successful login
   * @returns Promise that resolves with tokens when login is successful
   */
  openLoginPopup(redirectUrl?: string): angular.IPromise<IframeAuthTokens> {
    const deferred = this.$q.defer<IframeAuthTokens>();

    this.authLoggingApiService.log(
      'iframePopupAuthService: Opening popup for login'
    );

    // Build popup URL with mode parameter
    const currentOrigin = this.$window.location.origin;
    const popupUrl = `${currentOrigin}/login?mode=popup${
      redirectUrl ? `&redirectUrl=${encodeURIComponent(redirectUrl)}` : ''
    }`;

    // Calculate popup position (centered)
    const left = (this.$window.screen.width - POPUP_WIDTH) / 2;
    const top = (this.$window.screen.height - POPUP_HEIGHT) / 2;

    // Open popup
    const popup = this.$window.open(
      popupUrl,
      'flowingly-login-popup',
      `width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`
    );

    if (!popup) {
      this.authLoggingApiService.log(
        'iframePopupAuthService: Popup was blocked by browser'
      );
      deferred.reject(
        new Error('Popup was blocked. Please allow popups for this site.')
      );
      return deferred.promise;
    }

    // Track if tokens were received before popup closed
    let tokenReceived = false;

    // Set up message listener for tokens from popup
    const messageHandler = (event: MessageEvent) => {
      // Verify origin
      if (event.origin !== currentOrigin) {
        return;
      }

      if (event.data?.type === POPUP_AUTH_MESSAGE_TYPE) {
        this.authLoggingApiService.log(
          'iframePopupAuthService: Received auth tokens from popup'
        );

        this.$window.removeEventListener('message', messageHandler);
        tokenReceived = true;

        const tokens: IframeAuthTokens = {
          idToken: event.data.idToken,
          accessToken: event.data.accessToken,
          refreshToken: event.data.refreshToken,
          expiresIn: event.data.expiresIn
        };

        deferred.resolve(tokens);
      } else if (event.data?.type === 'FLOWINGLY_IFRAME_AUTH_ERROR') {
        this.authLoggingApiService.log(
          `iframePopupAuthService: Received error from popup: ${event.data.error}`
        );

        this.$window.removeEventListener('message', messageHandler);
        tokenReceived = true; // Prevent "popup closed" error
        deferred.reject(
          new Error(event.data.error || 'Authentication failed in popup')
        );
      }
    };

    this.$window.addEventListener('message', messageHandler);

    // Monitor popup close without successful login
    const pollTimer = setInterval(() => {
      if (popup.closed) {
        clearInterval(pollTimer);
        this.$window.removeEventListener('message', messageHandler);

        // Check if we already resolved (tokens received)
        if (!tokenReceived) {
          this.authLoggingApiService.log(
            'iframePopupAuthService: Popup closed without completing login'
          );
          deferred.reject(new Error('Login popup was closed'));
        }
      }
    }, 500);

    return deferred.promise;
  }

  /**
   * Initialize popup mode detection.
   * Should be called early in app startup to set the sessionStorage marker
   * if we're in a popup window (detected via mode=popup URL param).
   * This marker persists across Auth0 redirect.
   */
  initPopupMode(): void {
    const urlParams = new URLSearchParams(this.$window.location.search);
    if (urlParams.get('mode') === 'popup') {
      sessionStorage.setItem(POPUP_MODE_STORAGE_KEY, 'true');
    }
  }

  /**
   * Check if current page load is a popup callback (after Auth0 redirect).
   * We detect this by checking if:
   * 1. We have an opener window OR we have the popup mode marker in sessionStorage
   * 2. We have an authorization code in the URL (meaning Auth0 redirected back)
   */
  isPopupCallback(): boolean {
    const hasOpener = !!this.$window.opener;
    const hasPopupMarker =
      sessionStorage.getItem(POPUP_MODE_STORAGE_KEY) === 'true';
    const urlParams = new URLSearchParams(this.$window.location.search);
    const hasAuthCallback =
      urlParams.has('state') &&
      (urlParams.has('code') || urlParams.has('error'));

    return (hasOpener || hasPopupMarker) && hasAuthCallback;
  }

  /**
   * Check if we're in a popup window that was opened for authentication.
   * Only returns true when the sessionStorage marker was set by initPopupMode()
   * (i.e. the window was opened with mode=popup in the URL).
   * We intentionally do NOT check window.opener here because any window.open()
   * call (e.g. opening a process map in a new tab) sets window.opener, and
   * that would incorrectly trigger the popup auth flow and close the tab.
   */
  isInPopupWindow(): boolean {
    return sessionStorage.getItem(POPUP_MODE_STORAGE_KEY) === 'true';
  }

  /**
   * Handle the callback in the popup window after successful Auth0 login.
   * Manually exchanges the authorization code for tokens (bypassing Auth0 SDK to avoid third-party cookie issues),
   * then sends tokens back to the opener (iframe) via postMessage and closes the popup.
   */
  async handlePopupCallback(): Promise<void> {
    const hasOpener = !!this.$window.opener;
    const hasPopupMarker =
      sessionStorage.getItem(POPUP_MODE_STORAGE_KEY) === 'true';

    if (!hasOpener && !hasPopupMarker) {
      return;
    }

    // If we don't have an opener but have the marker, we can't send tokens back
    if (!hasOpener) {
      sessionStorage.removeItem(POPUP_MODE_STORAGE_KEY);
      alert(
        'Authentication completed but could not communicate with the parent window. Please close this popup and try again.'
      );
      this.$window.close();
      return;
    }

    try {
      const urlParams = new URLSearchParams(this.$window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');

      if (error) {
        throw new Error(errorDescription || error);
      }

      if (!code) {
        throw new Error('No authorization code found in URL');
      }

      // Retrieve PKCE code_verifier from sessionStorage
      const codeVerifier = this.getCodeVerifierFromStorage(state);

      if (!codeVerifier) {
        throw new Error('No code verifier found - PKCE verification will fail');
      }

      // Exchange the authorization code for tokens via Auth0's token endpoint
      // Use the current pathname as redirect_uri — Auth0 requires it to exactly match
      // the redirect_uri used in the authorization request (loginWithRedirect).
      // After Auth0 callback, the browser is already at the configured redirect_uri path.
      const tokenResponse = await this.$http<Auth0TokenResponse>({
        method: 'POST',
        url: `https://${this.APP_CONFIG.auth0Domain}/oauth/token`,
        headers: {
          'Content-Type': 'application/json'
        },
        data: {
          grant_type: 'authorization_code',
          client_id: this.APP_CONFIG.auth0AppV2ClientId,
          code: code,
          code_verifier: codeVerifier,
          redirect_uri:
            this.$window.location.origin + this.$window.location.pathname
        }
      });

      const tokens = tokenResponse.data;

      const message = {
        type: POPUP_AUTH_MESSAGE_TYPE,
        idToken: tokens.id_token,
        accessToken: tokens.access_token,
        refreshToken: tokens.refresh_token,
        expiresIn: tokens.expires_in
      };

      // Send to opener
      this.$window.opener.postMessage(message, this.$window.location.origin);

      // Clean up and close popup
      this.cleanupPkceStorage();
      sessionStorage.removeItem(POPUP_MODE_STORAGE_KEY);
      setTimeout(() => {
        this.$window.close();
      }, 100);
    } catch (error) {
      // Send error to opener
      this.$window.opener.postMessage(
        {
          type: 'FLOWINGLY_IFRAME_AUTH_ERROR',
          error: error.message
        },
        this.$window.location.origin
      );
      setTimeout(() => {
        this.$window.close();
      }, 100);
    }
  }

  /**
   * Get the message type constant
   */
  getMessageType(): string {
    return POPUP_AUTH_MESSAGE_TYPE;
  }

  /**
   * Retrieve the PKCE code_verifier from sessionStorage.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private getCodeVerifierFromStorage(_state: string): string | null {
    try {
      // Auth0 SPA SDK stores transaction data with the client ID
      const transactionKey = `a0.spajs.txs.${this.APP_CONFIG.auth0AppV2ClientId}`;
      const transactionData = sessionStorage.getItem(transactionKey);

      if (transactionData) {
        const transaction = JSON.parse(transactionData);
        if (transaction.code_verifier) {
          return transaction.code_verifier;
        }
      }

      // Fallback: search for any Auth0 transaction storage
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.includes('a0.spajs.txs')) {
          const data = sessionStorage.getItem(key);
          if (data) {
            const parsed = JSON.parse(data);
            if (parsed.code_verifier) {
              return parsed.code_verifier;
            }
          }
        }
      }

      return null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Clean up PKCE transaction storage after successful token exchange
   */
  private cleanupPkceStorage(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.includes('a0.spajs.txs')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch (e) {
      console.error(
        'iframePopupAuthService: Failed to clean up PKCE storage',
        e
      );
    }
  }
}

angular
  .module('flowingly.runner')
  .service('iframePopupAuthService', IframePopupAuthService);

export type IframePopupAuthServiceType = InstanceType<
  typeof IframePopupAuthService
>;
